import React, { useState, useEffect, useMemo } from 'react';
import { Tag, Space, ConfigProvider, Tabs } from 'antd';
import { CustomLoader } from '@components/UI/CustomLoader/Index.jsx';
import { EditOptionComp } from '@components/UI/EditOption/Index.jsx';
import { DeleteOptionComp } from '@components/UI/DeleteOption/Index.jsx';
import FilterView from '@components/UI/FilterView/Index.jsx';
import { StyledTable } from '@styles/datatables/datatable-antd.js';
import ptBR from 'antd/es/locale/pt_BR';
import TextBlock from '@components/UI/TextBlock/Index..jsx';
import { DeleteFormulario, GetAllFormularios } from '@services/Formulario/Index';
import { formatDateTimeToBR } from '@utils/FormatDate';
import { BlockedOptionComp } from '@components/UI/BlockedOption/Index';

const GridFormulariosModelos = ({ state, setData, openModal, setSelectedRow, onDelete }) => {
  const [filters, setFilters] = useState({
    codigo: '',
    titulo: '',
    nomeProcesso: '',
    nomeItem: '',
    flgStatus: '',
  });

  const filterLabels = {
    codigo: 'CÓDIGO',
    titulo: 'TÍTULO',
    nomeProcesso: 'PROCESSO',
    nomeItem: 'ITEM DO PROCESSO',
    flgStatus: 'STATUS',
  };

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabKey, setTabKey] = useState('modelos');

  const fetchData = async (currentPage, currentRowsPerPage, filters = {}) => {
    setLoading(true);
    try {
      const response = await GetAllFormularios(currentPage, currentRowsPerPage, filters);
      setData(response.items, response.totalItems);
    } catch (error) {
      console.error('Erro ao buscar dados dos formulários modelos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, rowsPerPage, filters);
  }, [page, rowsPerPage, filters]);

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const filterByTab = (data) => {
    if (!data) return [];

    return data.filter(item => {
      const idProcesso = item.idProcesso ?? item.IdProcesso;
      const idSolicitacaoProcessoItem = item.idSolicitacaoProcessoItem ?? item.IdSolicitacaoProcessoItem;
      const flgModelo = item.flgModelo ?? item.FlgModelo;

      if (tabKey === 'modelos') {
        return flgModelo === true;
      } else if (tabKey === 'processos') {
        return idProcesso != null && idSolicitacaoProcessoItem == null;
      } else if (tabKey === 'itens') {
        return idSolicitacaoProcessoItem != null;
      }
      return true;
    });
  };

  const getConfirmMessage = (row) => {
    return (
      `Você tem certeza que deseja excluir o formulário <strong>${row.titulo}</strong>? 
    Esta ação não poderá ser desfeita e excluirá as associações de formulários.`
    )
  };

  // Só inclui a coluna de ações se a aba for "modelos"
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'CÓDIGO',
        dataIndex: 'codigo',
        key: 'codigo',
        sorter: (a, b) => a.codigo.localeCompare(b.codigo),
        width: '15%',
      },
      {
        title: 'TÍTULO',
        dataIndex: ['titulo'],
        key: 'titulo',
        width: '40%',
        sorter: (a, b) => a.json.title.localeCompare(b.json.title),
        render: (text) => (
          <TextBlock>
            {text}
          </TextBlock>
        ),
      },
      {
        title: 'DATA CRIAÇÃO',
        dataIndex: 'dataCriacao',
        key: 'dataCriacao',
        width: '20%',
        sorter: (a, b) => a.dataCriacao.localeCompare(b.dataCriacao),
        render: (text) => (
          <>{formatDateTimeToBR(text)}</>
        ),
      },
      {
        title: 'STATUS',
        dataIndex: 'flgStatus',
        key: 'flgStatus',
        sorter: (a, b) => a.flgStatus - b.flgStatus,
        render: (flgStatus) => (
          <Tag color={(flgStatus) === true ? 'green' : 'red'}>
            {(flgStatus) === true ? 'ATIVO' : 'INATIVO'}
          </Tag>
        ),
        width: '15%',
      }
    ];

    // Só mostra a coluna de ações se estiver na aba de modelos
    if (tabKey === "modelos") {
      baseColumns.push({
        title: 'AÇÕES',
        key: 'actions',
        width: '10%',
        render: (text, row) => {
          const flgModelo = row.flgModelo

          return flgModelo === true ? (
            <Space size="middle">
              <EditOptionComp
                row={row}
                setSelectedRow={setSelectedRow}
                openModal={openModal}
              />
              <DeleteOptionComp
                row={row}
                deleteFunction={DeleteFormulario}
                onDelete={onDelete}
                itemNameKey="titulo"
                getConfirmMessage={getConfirmMessage}
              />
            </Space>
          ) : (
            <BlockedOptionComp title="Ações disponíveis apenas para modelos." />
          );
        }
      });
    }

    return baseColumns;
  }, [tabKey, setSelectedRow, openModal, onDelete]);

  return (
    <div className="px-4">
      <FilterView
        filters={filters}
        setFilters={setFilters}
        filterLabels={filterLabels}
      />

      <div className="mt-3 mb-3">
        <h1>Lista de Formulários</h1>
      </div>

      <Tabs
        defaultActiveKey="modelos"
        activeKey={tabKey}
        onChange={(key) => setTabKey(key)}
        items={[
          { key: 'modelos', label: 'Modelos (Genéricos)' },
          { key: 'processos', label: 'Associados a Processos' },
          { key: 'itens', label: 'Associados a Itens do Processo' },
        ]}
      />

      <ConfigProvider locale={ptBR}>
        <StyledTable
          rowKey={"id"}
          columns={columns}
          dataSource={filterByTab(state.data)}
          loading={{
            spinning: loading,
            indicator: <CustomLoader />,
          }}
          pagination={{
            current: page,
            pageSize: rowsPerPage,
            total: state.totalRows,
            onChange: handlePageChange,
            showSizeChanger: true,
            onShowSizeChange: handlePageChange,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
          }}
          scroll={{ y: '20rem', x: 'max-content' }}
          locale={{
            emptyText: <h2 className="m-3">Nenhum registro disponível para exibição.</h2>,
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default GridFormulariosModelos;
